import { useTranslation } from 'react-i18next';
import type { AccountTransactionType } from '@hero/schema';
import { Badge } from '../../../ui/Badge/Badge';

export interface AccountTransactionTypeBadgeProps {
  type: AccountTransactionType;
}

/**
 * Per-type pill for an `AccountTransaction`. The colour table
 * groups types by intent:
 *
 *   - capture / payment / advance → primary tones (forward money
 *     motion)
 *   - void / adjustment / refund  → amber (reversals / fixes)
 *   - credit / cashback           → green (money in)
 *   - debit / debit-failure       → red / muted (money out / issue)
 *
 * Kept inline (rather than under `@hero/web-theme`) because this
 * enum only renders here today — we'll lift it into the theme
 * helper if a second consumer ever needs the same colour map.
 */
export function AccountTransactionTypeBadge({ type }: AccountTransactionTypeBadgeProps) {
  const { t } = useTranslation('m-account-transaction');

  const STYLE_BY_TYPE: Record<AccountTransactionType, string> = {
    leg_capture: 'bg-blue-50 text-blue-700 border-blue-200',
    leg_payment: 'bg-blue-50 text-blue-700 border-blue-200',
    leg_void: 'bg-amber-50 text-amber-700 border-amber-200',
    leg_adjustment: 'bg-amber-50 text-amber-700 border-amber-200',
    advance_payment: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    account_debit: 'bg-red-50 text-red-700 border-red-200',
    account_credit: 'bg-green-50 text-green-700 border-green-200',
    account_debit_payment_failure: 'bg-gray-100 text-gray-700 border-gray-200',
    refund: 'bg-amber-50 text-amber-700 border-amber-200',
    cashback: 'bg-green-50 text-green-700 border-green-200',
  };

  return (
    <Badge variant="secondary" size="default" className={STYLE_BY_TYPE[type]}>
      {t(`type.${type}`, { defaultValue: type })}
    </Badge>
  );
}
